import type { TruckTrailerCoupling } from '@chukua/common';
import { Alert, Button, DatePicker, Drawer, Form, Select } from 'antd';
import dayjs from 'dayjs';
import { ZoneSearchSelect } from '../../zones/public/ZoneSearchSelect';
import { useCoupleTrailer } from '../hooks/use-couple-trailer';
import { COUPLE_GROUP_LABELS, coupleDrawerLabels, type CoupleOption, type CoupleSubject } from '../policies/couple-options';
import { RigSilhouette } from './RigSilhouette';

interface CoupleTrailerDrawerProps {
  /** Stable (`useMemo`): who the drawer couples something to. */
  subject: CoupleSubject;
  /** Shown above the form when the truck is out on a trip: the trip keeps the trailer it set off with. */
  tripNotice?: string;
  onClose: () => void;
  onCoupled: (coupling: TruckTrailerCoupling) => void;
}

type CoupleState = ReturnType<typeof useCoupleTrailer>;

/** Draws only the vehicle being picked: the trailer when a truck is being given one, the truck when a trailer is being put on one. */
function OptionRow({ option, picking }: { option: CoupleOption; picking: 'truck' | 'trailer' }) {
  const truck = picking === 'truck' ? option.rig.truck : null;
  const trailer = picking === 'trailer' ? option.rig.trailer : null;
  return (
    <div className="flex items-center gap-3 py-0.5">
      <RigSilhouette truck={truck} trailer={trailer} className="h-5 w-20 shrink-0" />
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="font-mono text-[13px]">{option.label}</span>
        <span className="truncate text-xs text-muted">{option.note}</span>
      </div>
    </div>
  );
}

function groupedOptions(options: CoupleOption[]) {
  return (['parked', 'other_truck', 'no_fit'] as const)
    .map((group) => ({ label: COUPLE_GROUP_LABELS[group], options: options.filter((option) => option.group === group) }))
    .filter((group) => group.options.length > 0);
}

function Picker({ couple, picking, label, placeholder }: { couple: CoupleState; picking: 'truck' | 'trailer'; label: string; placeholder: string }) {
  return (
    <Form.Item label={label} htmlFor="couple-pick">
      <Select
        {...couple.selectProps}
        id="couple-pick"
        value={couple.picked?.value}
        onChange={(value: string) => couple.setPickedId(value)}
        options={groupedOptions(couple.selectProps.options)}
        optionRender={(option) => {
          const found = couple.selectProps.options.find((candidate) => candidate.value === option.value);
          return found ? <OptionRow option={found} picking={picking} /> : option.label;
        }}
        placeholder={placeholder}
        notFoundContent={couple.selectProps.loading ? 'Searching…' : 'Nothing matches'}
      />
    </Form.Item>
  );
}

function CoupledAtField({ couple }: { couple: CoupleState }) {
  return (
    <Form.Item label="Coupled at" htmlFor="couple-at" extra="Leave empty for now.">
      <DatePicker
        id="couple-at"
        className="w-full"
        showTime={{ format: 'HH:mm' }}
        format="D MMM YYYY, HH:mm"
        value={couple.coupledAt}
        onChange={couple.setCoupledAt}
        disabledDate={(day) => day.isAfter(dayjs(), 'day')}
      />
    </Form.Item>
  );
}

/**
 * Couple a trailer to a truck, or swap the one it pulls (gitaumoses4/chukua#390). The picker lists what fits first and greys
 * what doesn't with the reason; taking a trailer off a truck here asks where it is dropped.
 */
export function CoupleTrailerDrawer({ subject, tripNotice, onClose, onCoupled }: CoupleTrailerDrawerProps) {
  const couple = useCoupleTrailer(subject, onCoupled);
  const labels = coupleDrawerLabels(subject, couple.picked);
  const submit = async () => {
    if (await couple.submit()) onClose();
  };

  return (
    <Drawer
      open
      title={labels.title}
      onClose={onClose}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={couple.submitting} disabled={!couple.picked} onClick={submit}>
            {labels.submit}
          </Button>
        </div>
      }
    >
      <Form layout="vertical" requiredMark={false}>
        {tripNotice && <Alert className="mb-4" type="info" showIcon message={tripNotice} />}
        <Picker couple={couple} picking={subject.kind === 'truck' ? 'trailer' : 'truck'} label={labels.picker} placeholder={labels.placeholder} />
        {labels.takenFrom && <Alert className="mb-4" type="warning" showIcon message={labels.takenFrom} />}
        {couple.needsDropZone && (
          <Form.Item label={labels.dropQuestion} htmlFor="couple-drop-zone" required>
            <ZoneSearchSelect id="couple-drop-zone" value={couple.dropZoneCode} onChange={couple.setDropZoneCode} />
          </Form.Item>
        )}
        <CoupledAtField couple={couple} />
        {couple.error && <Alert type="error" showIcon message={couple.error} />}
      </Form>
    </Drawer>
  );
}
